import {
  AddonKey as SalesOrderAddonKey,
  LineItem,
  LineItemInterface,
  Revenue,
  RevenueComponent,
  RevenuePeriod,
} from '@vendasta/sales-orders';
import {
  AddonKey as MarketplaceAddonKey,
  Currency as MarketplaceCurrency,
  Frequency,
  Package as MarketplacePackage,
  Price,
  Pricing,
} from '@vendasta/marketplace-packages';
import { AppKey } from '@galaxy/marketplace-apps';
import { ProductPricing, Currency as BillingCurrency, Frequency as BillingFrequency } from '@galaxy/billing';

export function convertMarketplacePricingToSalesOrderRevenue(pricing: Pricing): Revenue {
  return pricing && pricing.prices
    ? new Revenue({
        revenueComponents: pricing.prices.map(
          (price) =>
            new RevenueComponent({
              value: price.price > 0 ? price.price : 0,
              period: convertMarketplaceFrequencyToSalesOrderRevenuePeriod(price.frequency),
              isStartingRevenue: price.isStartingPrice || false,
            }),
        ),
      })
    : new Revenue();
}

export function convertBillingRetailPriceToSalesOrderRevenue(productPricing: ProductPricing): Revenue {
  return productPricing
    ? new Revenue({
        revenueComponents: [
          new RevenueComponent({
            value: productPricing.pricingRules?.length > 0 ? productPricing.pricingRules[0].price : 0,
            period: convertBillingFrequencyToSalesOrderRevenuePeriod(productPricing.frequency),
            isStartingRevenue: false,
          }),
        ],
      })
    : new Revenue();
}

export function convertMarketplaceCurrencyToCurrencyCode(currency: MarketplaceCurrency): string {
  if (MarketplaceCurrency[currency]) {
    return MarketplaceCurrency[currency];
  }
  return 'USD';
}

export function convertBillingCurrencyToCurrencyCode(currency: BillingCurrency): string {
  if (BillingCurrency[currency]) {
    return BillingCurrency[currency];
  }
  return 'USD';
}

export function convertMarketplaceFrequencyToSalesOrderRevenuePeriod(freq: Frequency): RevenuePeriod {
  switch (freq) {
    case Frequency.ONCE:
      return RevenuePeriod.ONETIME;
    case Frequency.DAILY:
      return RevenuePeriod.DAILY;
    case Frequency.WEEKLY:
      return RevenuePeriod.WEEKLY;
    case Frequency.MONTHLY:
      return RevenuePeriod.MONTHLY;
    case Frequency.YEARLY:
      return RevenuePeriod.YEARLY;
    default:
      // Default to monthly
      // Frequency.OTHER and RevenuePeriod.BIWEEKLY don't have associated counterparts.
      return RevenuePeriod.MONTHLY;
  }
}

export function convertBillingFrequencyToSalesOrderRevenuePeriod(freq: BillingFrequency): RevenuePeriod {
  switch (freq) {
    case BillingFrequency.OneTime:
      return RevenuePeriod.ONETIME;
    case BillingFrequency.Monthly:
      return RevenuePeriod.MONTHLY;
    case BillingFrequency.Yearly:
      return RevenuePeriod.YEARLY;
    default:
      // Default to monthly
      // Frequency.OTHER and RevenuePeriod.BIWEEKLY don't have associated counterparts.
      return RevenuePeriod.MONTHLY;
  }
}

export function convertMarketplaceAddonKeyToSalesOrderAddonKey(addonKey: MarketplaceAddonKey): SalesOrderAddonKey {
  return new SalesOrderAddonKey({
    appId: addonKey.appId,
    addonId: addonKey.addonId,
  });
}

export interface PackagedItem {
  package: MarketplacePackage;
  lineItem: LineItemInterface;
}

export function packagesToLineItems(pckgdItems: PackagedItem[]): LineItem[] {
  return pckgdItems.map((pkgdItem) => {
    const pckg = pkgdItem.package;
    const lineItem = pkgdItem.lineItem;
    return new LineItem({
      packageId: pckg.packageId,
      currencyCode: pckg.pricing ? convertMarketplaceCurrencyToCurrencyCode(pckg.pricing.currency) : 'USD',
      currentRevenue: convertMarketplacePricingToSalesOrderRevenue(pckg.pricing),
      quantity: lineItem?.quantity > 0 ? lineItem.quantity : 1,
    });
  });
}

export function addPricingToLineItems(
  lineItems: LineItemInterface[],
  priceByApp: { key: AppKey; pricing: ProductPricing }[],
): LineItem[] {
  return lineItems.map((li) => {
    const price = priceByApp.find((p) => p.key.appId === li.appKey.appId && p.key.editionId === li.appKey.editionId);
    const productPricing: ProductPricing = price ? price.pricing : null;
    const revenue = convertBillingRetailPriceToSalesOrderRevenue(productPricing);
    return new LineItem({
      appKey: li.appKey,
      currencyCode: convertBillingCurrencyToCurrencyCode(productPricing?.currency),
      currentRevenue: revenue,
      quantity: li.quantity > 0 ? li.quantity : 1,
      isTrial: li.isTrial,
    });
  });
}

export function convertCurrencyCodeToMPCurrency(currencyCode: string): MarketplaceCurrency {
  if (MarketplaceCurrency[currencyCode]) {
    return MarketplaceCurrency[currencyCode];
  }
  return MarketplaceCurrency.USD;
}

export function convertOrderRevenuePeriodToMPFrequency(revenuePeriod: RevenuePeriod): Frequency {
  // TODO: Unify Frequency between orders and packages
  switch (revenuePeriod) {
    case undefined: // proto doesn't transmit 0 enum, but the default should not be Onetime
    case RevenuePeriod.ONETIME:
      return Frequency.ONCE;
    case RevenuePeriod.DAILY:
      return Frequency.DAILY;
    case RevenuePeriod.WEEKLY:
      return Frequency.WEEKLY;
    case RevenuePeriod.MONTHLY:
      return Frequency.MONTHLY;
    case RevenuePeriod.YEARLY:
      return Frequency.YEARLY;
    case RevenuePeriod.BIWEEKLY: // No MP equivalent...?
    default:
      return Frequency.OTHER;
  }
}

export function getPackagePricingFromLineItem(lineItem: LineItem): Pricing {
  let prices: Price[] = [];
  if (!!lineItem.currentRevenue && !!lineItem.currentRevenue.revenueComponents) {
    prices = lineItem.currentRevenue.revenueComponents.map((c) => {
      return new Price({
        price: c.value,
        frequency: convertOrderRevenuePeriodToMPFrequency(c.period),
      });
    });
  }

  return new Pricing({
    currency: convertCurrencyCodeToMPCurrency(lineItem.currencyCode),
    prices: prices,
  });
}
